import { useEffect, useCallback } from 'react'
import copy from 'copy-to-clipboard'

import { keyWithModifierAndShift, shortcuts } from '@sketch/utils'
import { useToast } from '@sketch/toasts'

import { CanvasLinks, useCanvasHost } from '../host'
import { useCanvasSelection } from '@sketch/canvas'
import { useCanvasScope } from '../scope'

type LinkType = 'frame' | 'page' | 'section'

interface CopyLinkResult {
  url: string
  type: LinkType
}

/**
 * Sets up a global keyboard shortcut (Cmd+Shift+C on Mac, Ctrl+Shift+C on
 * Windows) that copies the URL of whatever the canvas is currently showing:
 *
 * 1. in a frame or Section focus view, that entity;
 * 2. on the page canvas with a top-level entity selected, that entity;
 * 3. otherwise the page itself.
 *
 * Which of the three applies comes from the canvas scope rather than from
 * matching the route here - the scope answers it once for every consumer, and
 * carries the page the canvas is on, including the fallbacks the route omits
 * (the Share document route and the root Preview route both leave the page out
 * of the URL).
 */
export function useCopyLinkShortcut(): void {
  const { showToast } = useToast()
  const scope = useCanvasScope()
  const host = useCanvasHost()
  const { selectedEntityUUID, selectedEntityKind } = useCanvasSelection()

  const createUrl = useCallback((): CopyLinkResult | null => {
    if (!scope || !host) {
      return null
    }

    // Canonical rather than version-preserving: a copied link is for someone
    // else, and should land them on the document rather than on whichever
    // version happened to be open here.
    const links = host.canonicalLinks
    const { pageUUID, focus } = scope

    // A focus view copies the entity it is focused on.
    if (focus.kind !== 'page') {
      return buildResult(links, focus.kind, pageUUID, focus.uuid)
    }

    // On the page canvas a selected top-level entity wins over the page itself.
    // The selection carries its own kind, so the frame and Section links part
    // ways here without having to go back to the file's Section list.
    if (selectedEntityUUID && selectedEntityKind) {
      return buildResult(
        links,
        selectedEntityKind,
        pageUUID,
        selectedEntityUUID
      )
    }

    const url = links.page(pageUUID)
    return url ? { url, type: 'page' } : null
  }, [scope, host, selectedEntityUUID, selectedEntityKind])

  useEffect((): (() => void) => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (!keyWithModifierAndShift(shortcuts.copyLink)(event)) {
        return
      }

      event.preventDefault()

      const result: CopyLinkResult | null = createUrl()
      if (result) {
        copy(`${window.location.origin}${result.url}`)
        showToast(
          result.type === 'frame'
            ? 'Copied link to frame'
            : result.type === 'section'
              ? 'Copied link to section'
              : 'Copied link to page'
        )
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [createUrl, showToast])
}

function buildResult(
  links: CanvasLinks,
  kind: 'frame' | 'section',
  pageUUID: string,
  entityUUID: string
): CopyLinkResult | null {
  const url =
    kind === 'section'
      ? links.section(pageUUID, entityUUID)
      : links.frame(pageUUID, entityUUID)

  return url ? { url, type: kind } : null
}
